import React, { useMemo } from 'react';

import Box from 'components/elements/Box';
import Button from 'components/elements/button';

import { ReactComponent as IconPlus } from 'icons/icon-plus.svg';

import { EntityTypes } from 'context/entityContext';
import CustomListListItem from './CustomListListItem';
import { TEntityInfo, ICustomList } from './shared';
import { PromiseState } from 'react-refetch';
import TableLoader from 'components/utility/ContentLoader/TableLoader/TableLoader';
import NoDataListItem from './NoDataListItem';
import ListViewContainer from '../shared/ListViewContainer';
import Span from 'components/elements/span';
import { CreateNewListButton } from './styles';
interface IAddToListProps {
  entityId: string;
  entityName: string;
  entityType: EntityTypes;
  close: () => void;
  switchToCreateListView: () => void;
  addEntityToList: (entities: TEntityInfo[], listId: string) => any;
  deleteEntityFromList: (entities: TEntityInfo[], listId: string) => any;
  customListsData: PromiseState<any>;
  error?: string;
}

const AddToList: React.FC<IAddToListProps> = (props) => {
  const {
    entityId,
    entityName,
    close,
    entityType,
    addEntityToList,
    deleteEntityFromList,
    customListsData,
    switchToCreateListView: createNewList,
    error,
  } = props;

  const { pending } = customListsData;

  const customLists: ICustomList[] | undefined = useMemo(() => {
    if (customListsData.fulfilled) {
      return customListsData.value?.lists?.sort(
        (first: ICustomList, second: ICustomList): number => {
          return (second.lastUpdatedAt ?? 0) - (first.lastUpdatedAt ?? 0);
        }
      );
    }
  }, [customListsData]);

  const onAdd = (list: ICustomList): (() => void) => {
    return (): void => {
      addEntityToList([{ id: entityId, type: entityType }], list.id);
    };
  };

  const onDelete = (list: ICustomList): (() => void) => {
    return (): void => {
      deleteEntityFromList([{ id: entityId, type: entityType }], list.id);
    };
  };

  const isInlist = (entityId: string, list: ICustomList): boolean => {
    return list.entities
      ? list.entities.some(
          (entity: TEntityInfo): boolean => entity.id === entityId
        )
      : false;
  };

  const showList = Boolean(customLists && customLists.length && !pending);
  const showNoData = Boolean(customLists && !customLists.length && !pending);

  return (
    <ListViewContainer
      title={`Add ${entityName} to list`}
      rejectedMessage={
        error ? 'An error occurred, please try again.' : undefined
      }
      close={close}
    >
      <Box maxHeight="25em" overflow="auto">
        {pending && (
          <table>
            <tbody>
              <TableLoader columnCount={2} />
            </tbody>
          </table>
        )}
        {showList && (
          <ul data-addtolist>
            {customLists?.map((list: ICustomList): any => (
              <CustomListListItem
                customList={list}
                key={list.id}
                entityIsInList={isInlist(entityId, list)}
                onAddClick={onAdd(list)}
                onDeleteClick={onDelete(list)}
              />
            ))}
          </ul>
        )}
        {showNoData && <NoDataListItem />}
      </Box>
      <section data-addtolist>
        <CreateNewListButton
          onClick={createNewList}
          data-pw="custom-lists-create-new-list"
        >
          <IconPlus />
          <Span fontWeight="600">Create new list</Span>
        </CreateNewListButton>
        <Box
          mt={'1em'}
          display="flex"
          justifyContent="flex-start"
          alignContent="stretch"
          alignItems="flex-end"
          mb={'1em'}
        >
          <Button
            onClick={close}
            variant="primary"
            alignSelf="auto"
            marginLeft="auto"
            fontWeight="600"
            borderRadius="3px"
          >
            Done
          </Button>
        </Box>
      </section>
    </ListViewContainer>
  );
};

export default AddToList;
